import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Copy, QrCode } from '@phosphor-icons/react'
import merchantApi from '../../services/merchantApi'
import {
  DEFAULT_FORM,
  DESTINATION_OPTIONS,
  SOURCE_MEDIUM_OPTIONS,
  STATUS_OPTIONS,
  buildWhatsAppCustomerMessage,
  buildWhatsAppLink,
  getApiItem,
  getPayload,
  mapCampaignToForm,
  slugify,
} from './campaignLinkUtils'
import './CampaignLinkFormPage.scss'
import Header from '../../components/Header/Header'

const getDestinationPlaceholder = (type) => {
  if (type === 'WhatsApp Chat') return '+60 12-345 6789'
  if (type === 'Mini Program Home') return 'No detail required'
  if (type === 'Product Page') return 'Select product or enter product ID'
  if (type === 'Promotion Page') return 'Select promotion or enter promotion ID'
  if (type === 'Voucher Page') return 'Select voucher or enter voucher ID'
  return 'Select or enter destination'
}

export default function CampaignLinkFormPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = Boolean(id)
  const [form, setForm] = useState(DEFAULT_FORM)
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!isEdit) return

    const loadCampaign = async () => {
      setLoading(true)
      setError('')
      try {
        const response = await merchantApi.campaignLinks.show(id)
        setForm(mapCampaignToForm(getApiItem(response)))
      } catch (err) {
        setError(err.message || 'Unable to load campaign link.')
      } finally {
        setLoading(false)
      }
    }

    loadCampaign()
  }, [id, isEdit])

  const slug = form.slug || slugify(form.campaignName || 'your-campaign-slug')
  const smartLink = `https://wave.my/c/${slug}`
  const trackingCode = slug.toUpperCase().replace(/[^A-Z0-9]/g, '') || 'TRACKINGCODE'
  const whatsappNumber = form.destinationType === 'WhatsApp Chat' ? form.destinationDetail.replace(/[^0-9]/g, '') : '60123456789'
  const defaultWhatsAppText = form.destinationType === 'WhatsApp Chat' ? (form.defaultWhatsAppText || '').trim() : ''
  const firstResponseMessage = form.destinationType === 'WhatsApp Chat' ? (form.firstResponseMessage || '').trim() : ''
  const whatsappCustomerMessage = buildWhatsAppCustomerMessage(defaultWhatsAppText, trackingCode)
  const whatsappLink = buildWhatsAppLink(whatsappNumber, defaultWhatsAppText, trackingCode)
  const mediumOptions = SOURCE_MEDIUM_OPTIONS[form.trafficSource] || SOURCE_MEDIUM_OPTIONS.Others

  const pageTitle = isEdit ? 'Edit Campaign Link' : 'Create New Campaign Link'
  const pageSubtitle = isEdit ? 'Update your campaign link details.' : 'Create a new campaign link to share on social media or other channels.'

  const updateField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
      ...(field === 'campaignName' && !current.slug ? { slug: slugify(value) } : {}),
      ...(field === 'trafficSource' ? { medium: (SOURCE_MEDIUM_OPTIONS[value] || SOURCE_MEDIUM_OPTIONS.Others)[0] } : {}),
      ...(field === 'destinationType' && value === 'Mini Program Home' ? { destinationDetail: '', defaultWhatsAppText: '', firstResponseMessage: '' } : {}),
    }))
  }

  const canSave = useMemo(() => (
    form.campaignName &&
    form.destinationType &&
    slug &&
    (form.destinationType === 'Mini Program Home' || form.destinationDetail)
  ), [form, slug])

  const handleSubmit = async () => {
    if (!canSave || saving) return

    setSaving(true)
    setError('')

    try {
      const payload = getPayload({ ...form, slug })
      if (isEdit) {
        await merchantApi.campaignLinks.update(id, payload)
      } else {
        const response = await merchantApi.campaignLinks.create(payload)
        const created = getApiItem(response)
        if (created?.id) {
          navigate(`/marketing/links/${created.id}`)
          return
        }
      }
      navigate('/marketing/links')
    } catch (err) {
      setError(err.message || 'Unable to save campaign link.')
    } finally {
      setSaving(false)
    }
  }

  const copyText = async (value) => {
    try {
      await navigator.clipboard.writeText(value)
      alert('Copied to clipboard.')
    } catch (_) {
      alert(value)
    }
  }

  if (loading) {
    return <div className="campaign-form-page"><p>Loading campaign link...</p></div>
  }

  return (
    <div className="campaign-form-page">
      <Header
            showBack={true}
            onBack={() => navigate('/marketing/links')}
            items={[
              { label: 'Marketing', to: '/' },
              { label: 'Campaign Links', to: '/marketing/links' },
              { label: isEdit ? 'Edit' : 'Create', current: true },
            ]}
          />
      <div className="campaign-form-header">
        <div>
          <h1>{pageTitle}</h1>
          <p>{pageSubtitle}</p>
        </div>
        <div className="campaign-header-actions">
          <button className="campaign-btn campaign-btn-light" type="button" onClick={() => navigate('/marketing/links')}>Cancel</button>
          <button className="campaign-btn campaign-btn-primary" type="button" disabled={!canSave || saving} onClick={handleSubmit}>{saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Save Link'}</button>
        </div>
      </div>

      {error && <div className="campaign-error">{error}</div>}

      <div className="campaign-form-grid">
        <section className="campaign-form-card">
          <h2>1. Campaign Information</h2>
          <label>Campaign Name <span>*</span><input value={form.campaignName} onChange={(event) => updateField('campaignName', event.target.value)} placeholder="e.g. Neon Beat Flash Sale" /></label>
          <label>Campaign Description<textarea value={form.description} onChange={(event) => updateField('description', event.target.value)} placeholder="Optional" /></label>
          <label>Status <span>*</span><select value={form.status} onChange={(event) => updateField('status', event.target.value)}>{STATUS_OPTIONS.map((option) => <option key={option}>{option}</option>)}</select></label>
          <div className="campaign-two-col">
            <label>Start Date & Time <em>(Optional)</em><input type="datetime-local" value={form.startAt} onChange={(event) => updateField('startAt', event.target.value)} /></label>
            <label>End Date & Time <em>(Optional)</em><input type="datetime-local" value={form.endAt} onChange={(event) => updateField('endAt', event.target.value)} /></label>
          </div>
        </section>

        <section className="campaign-form-card">
          <h2>2. Destination</h2>
          <label>Destination Type <span>*</span><select value={form.destinationType} onChange={(event) => updateField('destinationType', event.target.value)}>{DESTINATION_OPTIONS.map((option) => <option key={option}>{option}</option>)}</select></label>
          <label>{form.destinationType === 'WhatsApp Chat' ? 'WhatsApp Number' : 'Destination Detail'} {form.destinationType !== 'Mini Program Home' && <span>*</span>}<input value={form.destinationDetail} disabled={form.destinationType === 'Mini Program Home'} onChange={(event) => updateField('destinationDetail', event.target.value)} placeholder={getDestinationPlaceholder(form.destinationType)} /></label>
          {form.destinationType === 'WhatsApp Chat' && (
            <>
              <label>Default Text <em>(Optional)</em><textarea className="campaign-first-response-textarea" value={form.defaultWhatsAppText} onChange={(event) => updateField('defaultWhatsAppText', event.target.value)} placeholder="Hi, I'm interested in this campaign." /></label>
              <p className="campaign-helper-text">This pre-fills the customer's message in WhatsApp. W.A.V.E. will append the tracking code after two blank lines.</p>
              <label>First Response Message <em>(Optional)</em><textarea className="campaign-first-response-textarea" value={form.firstResponseMessage} onChange={(event) => updateField('firstResponseMessage', event.target.value)} placeholder={"⚡ FLASH SALE ACCESS GRANTED!\n\nYou have reserved access to this campaign. How many would you like?"} /></label>
            </>
          )}
          <p className="campaign-helper-text">The first response message is what W.A.V.E. sends automatically after the customer starts the conversation.</p>
        </section>

        <section className="campaign-form-card">
          <h2>3. Source & Channel</h2>
          <label>Traffic Source <em>(Optional)</em><select value={form.trafficSource} onChange={(event) => updateField('trafficSource', event.target.value)}>{Object.keys(SOURCE_MEDIUM_OPTIONS).map((option) => <option key={option}>{option}</option>)}</select></label>
          <label>Medium <em>(Optional)</em><select value={form.medium} onChange={(event) => updateField('medium', event.target.value)} disabled={form.trafficSource === 'Any / Unknown'}>{mediumOptions.map((option) => <option key={option}>{option}</option>)}</select></label>
        </section>

        <section className="campaign-form-card">
          <h2>4. Link Settings</h2>
          <label>Link Slug <span>*</span><div className="campaign-slug-input"><span>wave.my/c/</span><input value={form.slug} onChange={(event) => updateField('slug', slugify(event.target.value))} placeholder="your-campaign-slug" /></div></label>
          <label>Notes<textarea value={form.notes} onChange={(event) => updateField('notes', event.target.value)} placeholder="Optional" /></label>
        </section>
      </div>

      <section className="campaign-preview-card">
        <h2>Preview</h2>
        <div className="campaign-preview-grid">
          <div><span>Smart Link</span><strong>{smartLink}</strong><button type="button" onClick={() => copyText(smartLink)}><Copy size={16} /></button></div>
          <div><span>WhatsApp Link</span><strong>{whatsappLink}</strong><button type="button" onClick={() => copyText(whatsappLink)}><Copy size={16} /></button></div>
          <div><span>Customer First Message</span><strong>{whatsappCustomerMessage || 'No customer first message configured.'}</strong></div>
          <div><span>W.A.V.E. First Response</span><strong>{firstResponseMessage || 'No first response configured.'}</strong></div>
          <div className="campaign-qr-preview"><QrCode size={58} /><button className="campaign-btn campaign-btn-light" type="button">Download QR</button></div>
        </div>
      </section>
    </div>
  )
}
